import {PriceFunc, PriceElemsData, PriceSegment} from '@icc/price';
import {Injectable} from '@angular/core';
import { Cassonetto, ICCCassonetto } from '@icc/window-door/types';

@Injectable()
export class PriceCassonettoService {
    constructor() {}

    /**
     * Wylicza cenę kasonetek
     *
     * @param {number} price         Wejsciowa cena
     * @param {object} PriceElems    Składowe wyceny
     * @param {array}  NoPriceCauses Powody braku wyceny
     * @return {number} Cena z doliczoną dopłatą.
     */
    @PriceFunc({
        shortName: 'cassonetto',
        data: {
            cassonettos: 'conf.ComplementaryGoods.cassonetto',
            cassonettosData: 'data.cassonettos',
            pricelists: 'data.productPricelists'
        }
    })
    // eslint-disable-next-line max-statements
    cassonettoPrice(_priceElemsData: PriceElemsData, {cassonettos, cassonettosData, pricelists}: {cassonettos, cassonettosData, pricelists}): PriceSegment[] {
        const priceSegments: PriceSegment[] = [];

        let instanceCounter = 0;
        for (const cassonettoAccessory of cassonettos) {
            instanceCounter++;
            const cassonetto = cassonettoAccessory.accessory;
            const cassonettoData = cassonettosData.find((c: ICCCassonetto) => c.id === cassonetto.id);
            const priceList = this.getPriceListForSelectedColor(cassonetto);

            if (!priceList) {
                continue;
            }

            if (cassonettoData.celino) {
                this.addCelinoPriceSegments(priceSegments, cassonetto, instanceCounter, priceList, cassonettoData);
                continue;
            }

            const instanceId = `cassonetto_${cassonetto.id}_${instanceCounter}`;

            const baseData = {
                id: cassonetto.id,
                good: 'cassonetto',
                name: `${cassonetto.name} (${instanceCounter})`,
                count: cassonetto.count,
                color: Number(cassonetto.color.id),
                width: cassonetto.width,
                height: cassonetto.height,
                surface: this.getSurfaceArea(cassonetto),
                p1: cassonetto.p1,
                p2: cassonetto.p2,
                instanceId,
            };

            const basePrice = this.getBasePrice(cassonetto, pricelists);
            priceSegments.push(<PriceSegment>{
                type: 'complementaryGoods',
                baseValue: basePrice * cassonetto.count,
                value: basePrice * cassonetto.count,
                valueType: 'value',
                data: {...baseData, name: baseData.name + ' (raster)', p: basePrice, component: 'basePrice'},
            });

            const noFrontDiscountPercent = this.getNoFrontPlateDiscountPercent(cassonetto, cassonettoData);
            if (noFrontDiscountPercent !== null && noFrontDiscountPercent !== 0) {
                priceSegments.push(<PriceSegment>{
                    type: 'complementaryGoods',
                    baseValue: 1 + noFrontDiscountPercent / 100,
                    value: 1 + noFrontDiscountPercent / 100,
                    valueType: 'percent',
                    to: [{'data.component': 'basePrice', 'data.instanceId': instanceId}],
                    data: {...baseData, name: baseData.name + ' (brak płyty czołowej)', p: noFrontDiscountPercent},
                });
            }

            const colorSurchargePercent = this.getColorSurchargePercent(priceList);
            if (colorSurchargePercent !== null && colorSurchargePercent !== 0) {
                priceSegments.push(<PriceSegment>{
                    type: 'complementaryGoods',
                    baseValue: 1 + colorSurchargePercent / 100,
                    value: 1 + colorSurchargePercent / 100,
                    valueType: 'percent',
                    to: [{'data.component': 'basePrice', 'data.instanceId': instanceId}],
                    data: {...baseData, name: baseData.name + ' (dopłata za kolor)', p: colorSurchargePercent},
                });
            }


            const cuttingSurcharge = this.getCuttingSurcharge(cassonetto, cassonettoData, priceList);
            if (cuttingSurcharge !== null && cuttingSurcharge !== 0) {
                priceSegments.push(<PriceSegment>{
                    type: 'complementaryGoods',
                    baseValue: cuttingSurcharge * cassonetto.count,
                    value: cuttingSurcharge * cassonetto.count,
                    valueType: 'value',
                    data: {...baseData, name: baseData.name + ' (dopłata za skracanie)', p: cuttingSurcharge},
                });
            }

            const depthIncreaseSurcharge = this.getDepthIncreaseSurcharge(cassonetto, cassonettoData, priceList);
            if (depthIncreaseSurcharge !== null && depthIncreaseSurcharge !== 0) {
                priceSegments.push(<PriceSegment>{
                    type: 'complementaryGoods',
                    baseValue: depthIncreaseSurcharge * cassonetto.count,
                    value: depthIncreaseSurcharge * cassonetto.count,
                    valueType: 'value',
                    data: {...baseData, name: baseData.name + ' (dopłata za zw. głębokości)', p: depthIncreaseSurcharge},
                });
            }
        }
        return priceSegments;
    }


    cassonettoCalculatePrice(cassonetto: Cassonetto, cassonettoData: ICCCassonetto, productPricelists) {
        const priceList = this.getPriceListForSelectedColor(cassonetto);
        if (!priceList) {
            return null;
        }

        if (cassonettoData.celino) {
            return this.calculateCelinoPrice(cassonetto, priceList, cassonettoData);
        }

        const basePrice = this.getBasePrice(cassonetto, productPricelists);
        if (basePrice === null) {
            return null;
        }

        if (!this.validateP1P2(cassonetto, cassonettoData)) {
            return null;
        }

        const noFrontDiscountPercent = this.getNoFrontPlateDiscountPercent(cassonetto, cassonettoData);
        const colorSurchargePercent = this.getColorSurchargePercent(priceList);
        const cuttingSurcharge = this.getCuttingSurcharge(cassonetto, cassonettoData, priceList);
        const depthIncreaseSurcharge = this.getDepthIncreaseSurcharge(cassonetto, cassonettoData, priceList);

        if (cuttingSurcharge === null || depthIncreaseSurcharge === null) {
            return null;
        }

        const basePriceWithDiscounts = basePrice * (1 + noFrontDiscountPercent / 100) * (1 + colorSurchargePercent / 100);

        const totalUnitPrice = basePriceWithDiscounts +
            (cuttingSurcharge || 0) +
            (depthIncreaseSurcharge || 0);

        return totalUnitPrice;
    }

    private getBasePrice(cassonetto: Cassonetto, productPricelists) {
        const noPrice = null;
        if (typeof (cassonetto.color) === 'undefined') {
            return noPrice;
        }

        const productPriceList = this.getProductPriceList(cassonetto, productPricelists)
        if(!productPriceList) {
            return noPrice;
        }

        const priceList = this.getPriceListForSelectedColor(cassonetto)
        if (!priceList) {
            return noPrice;
        }

        const priceNode = productPriceList.data.find(node =>
            Number(node.height_from) <= Number(cassonetto.height)
            && Number(node.height_to) >= Number(cassonetto.height)
            && Number(node.width_from) <= Number(cassonetto.width)
            && Number(node.width_to) >= Number(cassonetto.width)
        );
        if (!priceNode) {
            return noPrice;
        }

        return Number(priceNode.price) || 0;
    }

    private validateP1P2(cassonetto: Cassonetto, cassonettoData: ICCCassonetto): boolean {
        if (cassonettoData.showP1 &&
            (
                typeof(cassonetto.p1) === 'undefined'
                || (cassonettoData.minP1Mm !== null && Number(cassonetto.p1) < Number(cassonettoData.minP1Mm))
                || (cassonettoData.maxP1Mm !== null && Number(cassonetto.p1) > Number(cassonettoData.maxP1Mm))
            )
        ) {
            return false;
        }

        if (cassonettoData.showP2 &&
            (
                typeof(cassonetto.p2) === 'undefined'
                || (cassonettoData.minP2Mm !== null && Number(cassonetto.p2) < Number(cassonettoData.minP2Mm))
                || (cassonettoData.maxP2Mm !== null && Number(cassonetto.p2) > Number(cassonettoData.maxP2Mm))
            )
        ) {
            return false;
        }

        return true;
    }

    private getSurfaceArea(cassonetto: Cassonetto): number {
        const L = Number(cassonetto.width) / 1000;
        const H = Number(cassonetto.height) / 1000;
        return L * H;
    }

    private getCelinoSurfaceArea(cassonetto: Cassonetto): number {
        const L = Number(cassonetto.width) / 1000;
        const P1 = Number(cassonetto.p1) / 1000;
        const surface = L * P1;
        return Math.ceil(surface * 100) / 100;
    }

    private getPerimeter(cassonetto: Cassonetto): number {
        const L = Number(cassonetto.width) / 1000;
        const H = Number(cassonetto.height) / 1000;
        return 2 * L + 2 * H;
    }

    private calculateCelinoPrice(cassonetto: Cassonetto, priceList: any, cassonettoData: ICCCassonetto): number | null {
        const basePrice = this.getCelinoBasePrice(cassonetto, priceList, cassonettoData);
        const colorSurchargePercent = this.getColorSurchargePercent(priceList);

        const totalPrice = basePrice * (1 + colorSurchargePercent / 100);

        return totalPrice;
    }

    private getNoFrontPlateDiscountPercent(cassonetto: Cassonetto, cassonettoData: ICCCassonetto): number {
        if (!cassonetto.noFrontPlate) {
            return 0;
        }
        const noFrontDiscount = Number(cassonettoData.noFrontPlateDiscount) || 0;
        return -noFrontDiscount;
    }

    private getColorSurchargePercent(priceList: any): number {
        const colorSurchargePercent = Number(priceList.colorSurchargePercent) || 0;
        return colorSurchargePercent;
    }


    // eslint-disable-next-line max-statements
    private getCuttingSurcharge(cassonetto: Cassonetto, cassonettoData: ICCCassonetto, priceList: any): number | null {
        let cuttingSurcharge = 0;

        if (cassonettoData.showP1) {
            const defaultDepthP1 = Number(cassonettoData.defaultP1) || 0;
            const maxDepthP1 = Number(cassonettoData.maxP1Mm) || 0;
            const currentDepthP1 = Number(cassonetto.p1) || defaultDepthP1;

            if (cassonettoData.allowDepthCuttingP1 && currentDepthP1 < defaultDepthP1) {
                const minDepthAfterCutting = Number(cassonettoData.minP1Mm) || 0;

                if (currentDepthP1 >= minDepthAfterCutting) {
                    const cuttingPricePerMb = Number(priceList.cuttingPricePerSqm) || 0;
                    cuttingSurcharge += cuttingPricePerMb;
                } else {
                    return null;
                }
            }

            if (cassonettoData.allowDepthIncreaseP1 && currentDepthP1 > defaultDepthP1 && currentDepthP1 < maxDepthP1) {
                const cuttingPricePerMb = Number(priceList.cuttingPricePerSqm) || 0;
                cuttingSurcharge += cuttingPricePerMb;
            }
        }

        if (cassonettoData.showP2) {
            const defaultDepthP2 = Number(cassonettoData.defaultP2) || 0;
            const maxDepthP2 = Number(cassonettoData.maxP2Mm) || 0;
            const currentDepthP2 = Number(cassonetto.p2) || defaultDepthP2;

            if (cassonettoData.allowDepthCuttingP2 && currentDepthP2 < defaultDepthP2) {
                const minDepthAfterCutting = Number(cassonettoData.minP2Mm) || 0;

                if (currentDepthP2 >= minDepthAfterCutting) {
                    const cuttingPricePerMb = Number(priceList.cuttingPricePerSqm) || 0;
                    cuttingSurcharge += cuttingPricePerMb;
                } else {
                    return null;
                }
            }
             if (cassonettoData.allowDepthIncreaseP2 && currentDepthP2 > defaultDepthP2 && currentDepthP2 < maxDepthP2) {
                const cuttingPricePerMb = Number(priceList.cuttingPricePerSqm) || 0;
                cuttingSurcharge += cuttingPricePerMb;
            }
        }

        return cuttingSurcharge;
    }

    private getDepthIncreaseSurcharge(cassonetto: Cassonetto, cassonettoData: ICCCassonetto, priceList: any): number | null {
        const perimeter = this.getPerimeter(cassonetto);
        let depthIncreaseSurcharge = 0;

        if (cassonettoData.showP1) {
            const defaultDepthP1 = Number(cassonettoData.defaultP1) || 0;
            const currentDepthP1 = Number(cassonetto.p1) || defaultDepthP1;

            const maxDepthAfterIncrease = Number(cassonettoData.maxP1Mm) || 0;
            if (cassonettoData.allowDepthIncreaseP1 && currentDepthP1 > defaultDepthP1 && currentDepthP1 <= maxDepthAfterIncrease) {
                const depthIncreasePricePerMb = Number(priceList.depthIncreasePricePerSqm) || 0;
                depthIncreaseSurcharge += depthIncreasePricePerMb * perimeter;
            } else if (currentDepthP1 > maxDepthAfterIncrease) {
                return null
            }
        }

        if (cassonettoData.showP2) {
            const defaultDepthP2 = Number(cassonettoData.defaultP2) || 0;
            const currentDepthP2 = Number(cassonetto.p2) || defaultDepthP2;

            const maxDepthAfterIncrease = Number(cassonettoData.maxP2Mm) || 0;
            if (cassonettoData.allowDepthIncreaseP2 && currentDepthP2 > defaultDepthP2 && currentDepthP2 <= maxDepthAfterIncrease) {
                const depthIncreasePricePerMb = Number(priceList.depthIncreasePricePerSqm) || 0;
                depthIncreaseSurcharge += depthIncreasePricePerMb * perimeter;
            } else if (currentDepthP2 > maxDepthAfterIncrease) {
                return null
            }
        }

        return depthIncreaseSurcharge;
    }


    getProductPriceList(cassonetto: Cassonetto, productPricelists) {
        const listPrice =  this.getPriceListForSelectedColor(cassonetto);
        if(!listPrice) {
            return null;
        }
        return productPricelists[listPrice.productPricelistId]
    }

    getPriceListForSelectedColor(cassonetto: Cassonetto) {
        const selectedColorGroups = cassonetto?.color?.colorGroups;
        if(!selectedColorGroups) return null;

        return cassonetto.pricelists.find(priceList => selectedColorGroups.includes(Number(priceList.windowColorGroupId)))
    }

    private addCelinoPriceSegments(priceSegments: PriceSegment[], cassonetto: Cassonetto, instanceCounter: number, priceList: any, cassonettoData: ICCCassonetto): void {
        const celinoPrice = this.getCelinoBasePrice(cassonetto, priceList, cassonettoData);

        const instanceId = `cassonetto_${cassonetto.id}_${instanceCounter}`;

        const baseData = {
            id: cassonetto.id,
            good: 'cassonetto',
            name: `${cassonetto.name} (${instanceCounter})`,
            count: cassonetto.count,
            color: Number(cassonetto.color.id),
            width: cassonetto.width,
            height: 0,
            surface: this.getCelinoSurfaceArea(cassonetto),
            p1: cassonetto.p1,
            p2: 0,
            instanceId,
        };

        priceSegments.push(<PriceSegment>{
            type: 'complementaryGoods',
            baseValue: celinoPrice * cassonetto.count,
            value: celinoPrice * cassonetto.count,
            valueType: 'value',
            data: {...baseData, name: baseData.name + ' (Celino)', p: celinoPrice, component: 'celinoPrice'},
        });

        const colorSurchargePercent = this.getColorSurchargePercent(priceList);
        if (colorSurchargePercent !== null && colorSurchargePercent !== 0) {
            priceSegments.push(<PriceSegment>{
                type: 'complementaryGoods',
                baseValue: 1 + colorSurchargePercent / 100,
                value: 1 + colorSurchargePercent / 100,
                valueType: 'percent',
                to: [{'data.component': 'celinoPrice', 'data.instanceId': instanceId}],
                data: {...baseData, name: baseData.name + ' (dopłata za kolor)', p: colorSurchargePercent},
            });
        }
    }

    private getCelinoBasePrice(cassonetto: Cassonetto, priceList: any, cassonettoData: ICCCassonetto) {
        const celinoPricePerSqm = Number(priceList.celinoPricePerSqm) || 0;
        if (celinoPricePerSqm === 0) {
            return 0;
        }

        const surface = this.getCelinoSurfaceArea(cassonetto);

        const minSurface = Number(cassonettoData.minCelinoSurfaceSqm) || 0;
        if (minSurface > 0 && surface < minSurface) {
            return minSurface * celinoPricePerSqm;
        }
        return surface * celinoPricePerSqm;
    }

}
